import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import { contextObject } from "../../components/shared/ShoppingCart/ShoppingCartContext";
import { useQuery } from "@apollo/client";
import { debounce, TextField } from "@mui/material";
import moment from "moment/moment";
import { useMutationCreateCart } from "../../queries/product/hooks/useMutationCreateCart";
import { NotificationType } from "../../constants/global";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { UserContext } from "../../hooks/userContext";
import { useMutationUpdateCart } from "../../queries/product/hooks/useMutationUpdateCart";
import { CART, PRODUCTS_PAGINATED } from "../../queries/product/productTypeDefs";
import { ProductDialog } from "../../components/shared/ProductDialog/ProductDialog";
import { CustomColumnMenu } from "../../components/shared/CustomColumnMenu/CustomColumnMenu";
import {
  attachStickyColumnScrollListener,
  BuyGridPagination,
  getProductListGridColumns,
  mapProductsToGridRows,
  PRODUCT_LIST_PAGE_SIZE,
  productListDataGridSx,
} from "./buyProductGrid";
import { marketplaceBuyableWhere } from "./productListWhere";

export const Buy: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const { cart } = useContext(contextObject);

  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState(0);

  const baseWhere = marketplaceBuyableWhere;
  const [currentWhere, setCurrentWhere] = useState<any>(baseWhere);

  const [createCart, { loading: createCartLoading }] = useMutationCreateCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: "asc" },
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const [updateCart, { loading: updateCartLoading }] = useMutationUpdateCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: "asc" },
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const { data, loading: queryLoading } = useQuery(PRODUCTS_PAGINATED, {
    variables: {
      where: currentWhere,
      orderBy: { createdAt: "desc" },
      skip: page * PRODUCT_LIST_PAGE_SIZE,
      take: PRODUCT_LIST_PAGE_SIZE,
    },
    fetchPolicy: "no-cache",
    pollInterval: 5000,
  });

  const paginatedResult = data?.findManyProductPaginated;
  const lastKnownCount = useRef(0);
  if (paginatedResult?.totalCount != null) {
    lastKnownCount.current = paginatedResult.totalCount;
  }
  const totalRows = lastKnownCount.current;

  const search = useCallback(
    debounce((value: string) => {
      setPage(0);
      if (value !== "") {
        setCurrentWhere({
          ...baseWhere,
          OR: [
            {
              drugName: {
                contains: value,
                mode: "insensitive",
              },
            },
            {
              ndc: {
                contains: value,
                mode: "insensitive",
              },
            },
          ],
        });
      } else {
        setCurrentWhere(baseWhere);
      }
    }, 1000),
    []
  );

  const handleSearch = (value: string) => {
    setSearchInput(value);
    search(value);
  };

  useEffect(() => attachStickyColumnScrollListener(), []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get("productId");

    if (productId && paginatedResult?.products) {
      const product = paginatedResult.products.find((p: any) => p.id === parseInt(productId));

      if (product) {
        const productRow = {
          ...product,
          expirationDate: moment(product.expDate),
          available: product.quantity,
          priceUnit: Number(product.unitPrice),
          price: Number(product.price),
          packaging: product.quantityPerPack,
          packagingValue: product.packaging,
          packType: product.type,
          sealed: product.packageCondition,
          sealDetails: product.sealDetails,
        };

        const productParams = {
          id: product.id,
          row: productRow,
        };

        setParams(productParams);
        setOpen(true);
        setSearchInput(product.drugName);
        search(product.drugName);

        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [data, search]);

  const cartLoading = createCartLoading || updateCartLoading;

  const handleCart = (params: any, packageAvailable: number = 1) => {
    if (!cartLoading) {
      const requestData = {
        quantity: params.row.available,
        User: {
          connect: {
            id: +id,
          },
        },
        Product: {
          connect: {
            id: params.id,
          },
        },
      };
      const foundCartItem: any = cart.find((cartItem: any) => cartItem.item.id === params.id);
      const newCount = packageAvailable + (foundCartItem?.quantity || 0);
      if (newCount > params.row.available) {
        addToast({ type: NotificationType.Error, title: `max available count: ${params.row.available}` });
      } else {
        if (foundCartItem) {
          updateCart({ variables: { data: { ...requestData, quantity: { increment: packageAvailable } }, where: { id: foundCartItem.id } } })
            .then(() => {
              addToast({ type: NotificationType.Success, title: "Cart updated successfully" });
            })
            .catch((error: any) => {
              addToast({ type: NotificationType.Error, title: error.message });
            });
        } else {
          createCart({ variables: { data: { ...requestData, quantity: packageAvailable } } }).catch((error: any) => {
            addToast({ type: NotificationType.Error, title: error.message });
          });
        }
      }
    }
  };

  const columns = useMemo(
    () =>
      getProductListGridColumns({
        currentUserId: +id,
        updateCartLoading: cartLoading,
        handleCart,
      }),
    [id, cartLoading]
  );

  return (
    <div>
      <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", pb: 3 }}>
            <Typography component="h4" variant="h6">
              Buy
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "row", pb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <TextField
                type="text"
                placeholder="Search by Drug name or NDC..."
                value={searchInput}
                onChange={(event: any) => handleSearch(event.target.value)}
                fullWidth
                sx={{ minHeight: "30px", minWidth: "300px" }}
                size="small"
              />
            </Box>
          </Box>
          <Box sx={{ height: "calc(100vh - 236px)", width: "100%" }}>
            <DataGrid
              loading={loading || queryLoading}
              rows={mapProductsToGridRows(paginatedResult?.products)}
              columns={columns}
              slots={{ columnMenu: CustomColumnMenu, pagination: BuyGridPagination }}
              slotProps={{ pagination: { rowCount: totalRows } as any }}
              paginationMode="server"
              rowCount={totalRows}
              paginationModel={{ page, pageSize: PRODUCT_LIST_PAGE_SIZE }}
              onPaginationModelChange={(model) => setPage(model.page)}
              onRowClick={(rowParams, event: any) => {
                if (event.target.tagName === "DIV" || event.target.tagName === "SPAN") {
                  setParams(rowParams);
                  setOpen(true);
                }
              }}
              pageSizeOptions={[PRODUCT_LIST_PAGE_SIZE]}
              sx={productListDataGridSx}
            />
          </Box>
        </>
      </Paper>
      {open && (
        <ProductDialog
          params={params}
          setParams={setParams}
          open={open}
          setOpen={setOpen}
          handleCart={handleCart}
          loading={cartLoading}
        />
      )}
    </div>
  );
};

export default Buy;
